import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { SavedMentionSearchApiModel, SavedMentionSearchBatchApiModel } from './model/saved-mention-search.api.model';
import { SavedMentionSearchListItemApiModel } from './model/saved-mention-search-list-item-api.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';
import { NaturalLanguageWorkflowApiModel } from './model/natural-language-workflow-api.model';
import { NaturalLanguageWorkflowResponseApiModel } from './model/natural-language-workflow-response-api.model';

@Injectable()
export class SavedMentionSearchesApi extends MultiBaseAbstractApi<SavedMentionSearchApiModel, SavedMentionSearchListItemApiModel> {

    public static MAX_BATCH_SEARCH_SIZE: number = 20;

    constructor(
        private neptuneApi: NeptuneApi
    ) {
        super();
    }

    /**
     * Gets all saved mention searches assigned to the provided account ID
    */
    public getList(): Observable<SavedMentionSearchListItemApiModel[]> {
        const slug: string = '/saved-mention-searches/';
        return this.neptuneApi.api<SavedMentionSearchListItemApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Gets a saved mention search based on the provided ID
    */
    public getById(id: number): Observable<SavedMentionSearchApiModel> {
        const slug: string = `/saved-mention-searches/${id}`;
        return this.neptuneApi.api<SavedMentionSearchApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Gets multiple saved mention searches based on the provided IDs
    */
    public getSavedSearchBulk(ids: number[]): Observable<SavedMentionSearchBatchApiModel> {

        let startIndex  = 0;
        const slicedIds: number[][] = [];
        while (startIndex < ids.length) {
            slicedIds.push(ids.slice(startIndex, startIndex + SavedMentionSearchesApi.MAX_BATCH_SEARCH_SIZE));
            startIndex += SavedMentionSearchesApi.MAX_BATCH_SEARCH_SIZE;
        }
        const requests: Observable<SavedMentionSearchBatchApiModel>[] = [];
        slicedIds.forEach(s => {
            let slug: string = '/saved-mention-searches/batch';
            s.forEach(id => s.indexOf(id) === 0 ? slug += `?ids=${id}` : slug += `&ids=${id}`);
            requests.push(this.neptuneApi.api<SavedMentionSearchBatchApiModel>(
                slug,
                null,
                HttpVerbsEnum.GET
            ));
        });

        return forkJoin(requests).pipe(
            map<SavedMentionSearchBatchApiModel[], SavedMentionSearchBatchApiModel>(
                (results) => {
                    const merged: SavedMentionSearchBatchApiModel = {};
                    results.forEach(
                        (result) => {
                            for (const mentionId of Object.keys(result)) {
                                merged[mentionId] = result[mentionId];
                            }
                        }
                    );
                    return merged;

                }
            )
        );
    }

    /**
     * Creates a new saved mention search and returns the ID of the new saved search
    */
    public create(search: SavedMentionSearchApiModel): Observable<number> {
        const slug: string = '/saved-mention-searches/';
        return this.neptuneApi.api<number>(
            slug,
            search,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Creates a new saved mention search and returns the ID of the new saved search
    */
    public update(search: SavedMentionSearchApiModel): Observable<number> {
        const slug: string = `/saved-mention-searches/${search.id}`;
        return this.neptuneApi.api<number>(
            slug,
            search,
            HttpVerbsEnum.PUT
        );
    }

    /**
     * Deletes a saved mention search
    */
    public deleteById(id: number): Observable<number> {
        const slug: string = `/saved-mention-searches/${id}`;

        return this.neptuneApi.api<number>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    /**
     * Adds and/or removes saved mention searches from provided group
     * @param addIds Searches to add to group
     * @param removeIds Searches to remove from group
     * @param groupId
     * @returns
     */
    public updateSavedSearchDisplayGroup(addIds: number[], removeIds: number[], groupId: number): Observable<void> {
        const slug: string = `/saved-mention-searches/displaygroups/${groupId}`;
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes group ID from all provided saved mention searches
     * @param removeIds Searches to remove from groups
     * @returns
     */
    public removeSavedSearchesFromDisplayGroup(removeIds: number[]): Observable<void> {
        const slug: string = '/saved-mention-searches/displaygroups';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Adds and/or removes saved mention searches from favourites
     * @param addIds Searches to add to favourites
     * @param removeIds Searches to remove from favourites
     * @returns
     */
    public updateSavedSearchFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        const slug: string = '/saved-mention-searches/displaygroups/favorites';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    public generateSearchCriteriaFromNaturalLanguagePrompt(prompt: NaturalLanguageWorkflowApiModel): Observable<NaturalLanguageWorkflowResponseApiModel> {
        return this.neptuneApi.api<NaturalLanguageWorkflowResponseApiModel>(
            '/workflow/topic',
            prompt,
            HttpVerbsEnum.POST
        );
    }

}
